/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

/**
 * @module bulb-media-embed/bulbmediaembedui
 */

import { Plugin, ButtonView, type Locale } from 'ckeditor5';
import bulbVideoIcon from '../../assets/block_toolbar_icons/code-solid.svg';
import { assetInsertLocation } from '../utils';

/**
 * BulbMediaEmbedUI plugin. Handles our bulb media embed UI.
 *
 * Creates and handles the componentFactory to add to the cke toolbar, business logic, and button execution / state.
 */
export default class BulbMediaEmbedUI extends Plugin {

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbMediaEmbedUI' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    const { editor } = this;
    const { t } = editor;

    // to be displayed in the toolbar.
    editor.ui.componentFactory.add('bulbInsertMediaEmbed', (locale: Locale): ButtonView => {
      // The state of the button will be bound to the widget command.
      const command = editor.commands.get('insertEmbed');

      // The button will be an instance of ButtonView.
      const buttonView = new ButtonView(locale);

      buttonView.set({
        label: t('content_block_type_URL'),
        icon: bulbVideoIcon,
        tooltip: true,
        withText: true,
      });

      // Callback executed once the button is clicked:
      buttonView.on('execute', (event) => {
        const openAssetLibraryEvent = new CustomEvent('openEmbedUrlModal', {
          detail: {
            event,
            eventFiredFromEditor: true,
          },
        });

        window.dispatchEvent(openAssetLibraryEvent);
      });

      // Listen for the event that is going to be fired from our client that the upload was successful.
      window.addEventListener('bulbMediaEmbedResponse', (event: any) => {
        assetInsertLocation(this.editor);
        editor.execute('insertEmbed', event.detail.contentBlock);
      });

      // Bind the state of the button to the command.
      buttonView.bind('isOn', 'isEnabled').to(command as any, 'value', 'isEnabled');

      return buttonView;
    });
  }
}
